import { createContext, useContext } from "react";

import type { JDDetail, JDSkill } from "@/api/jd-queries";
import type { JDQuestion } from "@/api/jd-question-queries";

/**
 * The shape of a job description's unsaved edits and what a row shows once they are applied. Kept apart
 * from jd-draft.tsx, which holds the components, because react-refresh/only-export-components wants a
 * component file to export components only.
 */

export type SkillPatch = {
  importance?: JDSkill["importance"];
  category?: JDSkill["category"];
  stack_path?: string | null;
  concepts?: string[];
  status?: "ignored" | "restore";
};
export type LinkPatch = { is_pinned?: boolean; is_excluded?: boolean };

/** The Details step's fields as the form holds them: years as numbers, the expiry as a day. */
export interface Details {
  title: string;
  summary: string;
  client_name: string;
  show_client: boolean;
  experience_min: number | null;
  experience_max: number | null;
  expires_at: string | null;
  audience_members: string[];
  audience_teams: string[];
}

export const DETAIL_LABEL: Record<keyof Details, string> = {
  title: "title",
  summary: "summary",
  client_name: "client",
  show_client: "client visibility",
  experience_min: "experience",
  experience_max: "experience",
  expires_at: "expiry",
  audience_members: "who can practise it",
  audience_teams: "who can practise it",
};

export function savedDetails(jd: JDDetail): Details {
  return {
    title: jd.jd.title,
    summary: jd.summary ?? "",
    client_name: jd.jd.client_name ?? "",
    show_client: jd.jd.show_client,
    experience_min: jd.jd.experience_min,
    experience_max: jd.jd.experience_max,
    expires_at: jd.jd.expires_at ? jd.jd.expires_at.slice(0, 10) : null,
    audience_members: jd.jd.audience_members ?? [],
    audience_teams: jd.jd.audience_teams ?? [],
  };
}

export interface Draft {
  skills: Record<number, SkillPatch>;
  links: Record<number, LinkPatch>;
  details: Partial<Details>;
  policy: { id: number | null } | null;
  // Questions per bucket a pick for this JD returns; null inside is "clear them" (the caller decides).
  counts: { value: Record<string, number> | null } | null;
}

export const EMPTY: Draft = { skills: {}, links: {}, details: {}, policy: null, counts: null };

export interface DraftApi {
  count: number;
  /** What is unsaved, in a few words for the save bar: "experience, 2 skills". */
  summary: string;
  saving: boolean;
  error: unknown;
  skill: (skill: JDSkill) => JDSkill;
  skillPatched: (id: number) => boolean;
  patchSkill: (id: number, patch: SkillPatch) => void;
  /** Drops every unsaved change to one skill, so undoing it does not mean discarding the whole page. */
  resetSkill: (id: number) => void;
  link: (q: JDQuestion) => JDQuestion;
  patchLink: (id: number, patch: LinkPatch) => void;
  details: (saved: Details) => Details;
  patchDetails: (patch: Partial<Details>, saved: Details) => void;
  policyId: (saved: number | null) => number | null;
  setPolicy: (id: number | null, saved: number | null) => void;
  questionCounts: (saved: Record<string, number> | null) => Record<string, number> | null;
  setQuestionCounts: (value: Record<string, number> | null, saved: Record<string, number> | null) => void;
  save: () => Promise<void>;
  discard: () => void;
}

export const DraftContext = createContext<DraftApi | null>(null);

export function useDraft(): DraftApi {
  const api = useContext(DraftContext);
  if (!api) throw new Error("useDraft needs a DraftProvider above it");
  return api;
}

/** What a skill row shows once its draft is applied — the server's own rules, mirrored so the row
 * does not claim a mapping the save would clear (orchestration/jd.py:edit_skill). */
export function applySkill(skill: JDSkill, p: SkillPatch | undefined): JDSkill {
  if (!p) return skill;
  const next = { ...skill };
  if (p.importance) next.importance = p.importance;
  if (p.category) {
    next.category = p.category;
    if (p.category !== "technical" && p.category !== "domain") {
      next.stack_path = null;
      next.entity = null;
      next.status = "ignored";
    }
  }
  if ("stack_path" in p) {
    next.stack_path = p.stack_path ?? null;
    next.entity = null;
    next.status = p.stack_path ? "mapped" : "gap";
    if (p.stack_path !== skill.stack_path) next.concepts = [];
  }
  if (p.concepts) next.concepts = p.concepts;
  if (p.status === "ignored") next.status = "ignored";
  if (p.status === "restore") next.status = next.stack_path || next.entity ? "mapped" : "gap";
  return next;
}
